
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authHeaders } from '../api/client';
import GdprModal from '../components/GdprModal';

const API_URL = process.env.REACT_APP_API_URL || '';
const POLL_INTERVAL_MS = 3000;

// Track last processed code to prevent double-execution in StrictMode
// but allow subsequent login attempts with new codes
let lastProcessedCode = null;

const LinkedinCallback = ({ onLogin }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [showGdprModal, setShowGdprModal] = useState(false);
    const [pendingUser, setPendingUser] = useState(null);
    const [linkingState, setLinkingState] = useState(null); // { token, telegram }
    const pollRef = useRef(null);
    const pollFailuresRef = useRef(0);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    // Poll for linking status
    useEffect(() => {
        if (!linkingState) return;

        pollFailuresRef.current = 0;

        const pollStatus = async () => {
            if (!pollRef.current) return;
            try {
                const response = await fetch(`${API_URL}/api/auth/link-status/${linkingState.token}`);
                const data = await response.json();
                pollFailuresRef.current = 0;

                if (data.status === 'completed' && data.user) {
                    stopPolling();
                    if (data.user.consentGdpr) {
                        onLogin(data.user);
                        navigate('/');
                    } else {
                        setPendingUser(data.user);
                        setShowGdprModal(true);
                        setLinkingState(null);
                    }
                } else if (data.status === 'expired' || data.status === 'not_found') {
                    stopPolling();
                    setLinkingState(null);
                    setError('Linking expired. Please try again.');
                    setTimeout(() => navigate('/'), 3000);
                }
            } catch (e) {
                pollFailuresRef.current += 1;
                if (pollFailuresRef.current > 20) {
                    stopPolling();
                    setLinkingState(null);
                    setError('Connection lost. Please try again.');
                    setTimeout(() => navigate('/'), 3000);
                }
            }
        };

        pollRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
        return stopPolling;
    }, [linkingState, onLogin, navigate, stopPolling]);

    useEffect(() => {
        const processCallback = async () => {
            const params = new URLSearchParams(location.search);
            const code = params.get('code');
            const state = params.get('state');
            const err = params.get('error');

            // Prevent double-execution in Dev/StrictMode
            if (!code || code === lastProcessedCode) return;
            lastProcessedCode = code;

            if (err) {
                setError('Authentication declined or failed.');
                setTimeout(() => navigate('/'), 3000);
                return;
            }

            try {
                const redirectUri = window.location.origin + '/auth/linkedin/callback';

                // Explicit linking: if a session exists, the backend reads WHO
                // from the bearer token. It used to take `currentUserId` from
                // the body, which let anyone staple their LinkedIn to a member
                // id they knew and log in as them (fixed 2026-09-02).
                const response = await fetch(`${API_URL}/api/auth/linkedin`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...authHeaders('user') },
                    credentials: 'same-origin',
                    body: JSON.stringify({ code, state, redirectUri })
                });

                const data = await response.json();

                if (data.success) {
                    if (data.user.consentGdpr) {
                        onLogin(data.user);
                        navigate('/');
                    } else {
                        setPendingUser(data.user);
                        setShowGdprModal(true);
                    }
                } else if (data.duplicateDetected && data.linkingInitiated && data.linkingToken) {
                    // Magic link sent to Telegram — start polling
                    setLinkingState({
                        token: data.linkingToken,
                        telegram: data.potentialMatch?.telegram || 'Telegram'
                    });
                } else {
                    setError(data.message || 'Authentication failed');
                    setTimeout(() => navigate('/'), 3000);
                }
            } catch (e) {
                setError('Network error during authentication.');
                setTimeout(() => navigate('/'), 3000);
            }
        };

        processCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGdprAccept = async (modalData) => {
        if (!pendingUser) return;

        try {
            // pendingUser is not in localStorage yet (consent happens before login
            // completes), so the token travels from the login response directly.
            const payload = {
                linkedin: modalData?.linkedin || '',
                name: modalData?.name || '',
                family: modalData?.family || ''
            };

            const response = await fetch(`${API_URL}/api/consent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders('user', pendingUser) },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                const updatedUser = { ...pendingUser, consentGdpr: true };
                onLogin(updatedUser);
                navigate('/');
            } else {
                alert(t('form.consent_error'));
            }
        } catch (err) {
            alert(t('form.error_generic'));
        }
    };

    const handleGdprClose = () => {
        setShowGdprModal(false);
        navigate('/');
    };

    const handleCancelLinking = () => {
        stopPolling();
        setLinkingState(null);
        navigate('/');
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
            color: '#fff'
        }}>
            {showGdprModal && (
                <GdprModal
                    onAccept={handleGdprAccept}
                    onClose={handleGdprClose}
                    initialName={pendingUser?.firstName}
                    initialFamily={pendingUser?.lastName}
                />
            )}

            {!showGdprModal && linkingState && (
                <div style={{
                    textAlign: 'center',
                    maxWidth: '420px',
                    padding: '0 20px'
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📱</div>
                    <h2 style={{
                        fontSize: '1.4rem',
                        fontWeight: 600,
                        marginBottom: '12px',
                        color: '#fff'
                    }}>
                        Check your Telegram
                    </h2>
                    <p style={{
                        fontSize: '1rem',
                        color: 'rgba(255,255,255,0.8)',
                        lineHeight: 1.5,
                        marginBottom: '8px'
                    }}>
                        We found your existing account ({linkingState.telegram}).
                    </p>
                    <p style={{
                        fontSize: '1rem',
                        color: 'rgba(255,255,255,0.8)',
                        lineHeight: 1.5,
                        marginBottom: '24px'
                    }}>
                        We sent a confirmation to your Telegram. Tap <strong>"Yes, link my accounts"</strong> there to connect your LinkedIn.
                    </p>
                    <div className="spinner" style={{ marginBottom: '20px' }}></div>
                    <p style={{
                        fontSize: '0.85rem',
                        color: 'rgba(255,255,255,0.5)',
                        marginBottom: '24px'
                    }}>
                        Waiting for confirmation...
                    </p>
                    <button
                        onClick={handleCancelLinking}
                        style={{
                            background: 'transparent',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: 'rgba(255,255,255,0.7)',
                            padding: '8px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {!showGdprModal && !linkingState && (
                <>
                    {error ? (
                        <div style={{ color: '#ef4444', fontSize: '1.2rem' }}>{error}</div>
                    ) : (
                        <>
                            <div className="spinner" style={{ marginBottom: '20px' }}></div>
                            <div>Connecting with LinkedIn...</div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default LinkedinCallback;
